import apiService from '../../../services/api'
import { isDevelopmentMode } from '../../../utils/mockData'

/**
 * Telegram Stars Payment API Service
 * Сервис для работы с оплатой через Telegram Stars
 */

/**
 * Получить доступные пакеты кристаллов для Telegram Stars
 * @returns {Promise<Array>} Массив пакетов кристаллов
 */
export const fetchCrystalPackages = async () => {
  try {
    const response = await apiService.makeRequest('/api/topup/')
    
    if (response.topup && Array.isArray(response.topup)) {
      return response.topup
    } else {
      throw new Error('Invalid response format: topup array not found')
    }
  } catch (error) {
    console.error('Error fetching crystal packages:', error)
    throw error
  }
}

/**
 * Создать инвойс для оплаты через Telegram Stars
 * @param {number} crystals - Количество кристаллов для покупки
 * @param {number} stars - Количество звезд для списания (не используется, но оставлен для совместимости)
 * @returns {Promise<{invoice_url: string}>}
 */
export const createInvoice = async (crystals, stars) => {
  try {
    const response = await apiService.makeRequest(`/api/invoice/${crystals}/`)

    if (response.invoice) {
      return {
        invoice_url: response.invoice
      }
    } else {
      throw new Error('Invalid response format from payment API')
    }
  } catch (error) {
    console.error('Error creating invoice:', error)
    throw error
  }
}

/**
 * Проверить статус платежа по ID (legacy метод, может понадобиться)
 * @param {string} paymentId - ID платежа для проверки
 * @returns {Promise<{status: string, balance?: number}>}
 */
export const checkPaymentStatus = async (paymentId) => {
  try {
    const response = await apiService.makeRequest(`/api/payment/status/${paymentId}/`)
    
    return {
      status: response.status, // 'pending', 'completed', 'failed'
      balance: response.balance || null
    }
  } catch (error) {
    console.error('Error checking payment status:', error)
    throw error
  }
}

/**
 * Открыть окно оплаты Telegram Stars с обработкой результата через событие и callback
 * @param {string} invoiceUrl - URL инвойса от Telegram
 * @param {Function} onPaymentResult - Callback функция для обработки результата оплаты
 */
export const openTelegramPayment = (invoiceUrl, onPaymentResult) => {
  // Mock оплата для разработки - симулируем успешный результат через 2 секунды
  if (isDevelopmentMode()) {
    console.log('🔧 Development mode: Mock payment initiated')
    setTimeout(() => {
      const mockResult = { success: true }
      console.log('🔧 Development mode: Mock payment completed successfully')
      onPaymentResult(mockResult)
    }, 2000)
    return
  }

  // Проверка доступности Telegram WebApp API
  if (!window.Telegram?.WebApp?.openInvoice) {
    console.error('❌ Telegram WebApp API недоступен')
    onPaymentResult({ success: false, error: 'Telegram WebApp API недоступен' })
    return
  }

  const tg = window.Telegram.WebApp
  let callbackExecuted = false

  // Обработчик события invoiceClosed (основной способ)
  const handleInvoiceClosed = (eventData) => {
    console.log('📱 Invoice closed event:', eventData)
    
    if (callbackExecuted) return // Предотвращаем двойной вызов
    callbackExecuted = true
    
    // Убираем обработчик после использования
    tg.offEvent('invoiceClosed', handleInvoiceClosed)
    
    // Определяем успешность по статусу
    const success = eventData?.status === 'paid'
    onPaymentResult({ 
      success, 
      status: eventData?.status,
      error: success ? null : 'Платеж не завершен'
    })
  }

  // Callback функция (резервный способ)
  const callbackHandler = (status) => {
    console.log('📱 Invoice callback:', status)
    
    if (callbackExecuted) return // Предотвращаем двойной вызов
    callbackExecuted = true
    
    const success = status === 'paid'
    onPaymentResult({ 
      success, 
      status,
      error: success ? null : 'Платеж не завершен'
    })
  }

  // Устанавливаем обработчик события
  tg.onEvent('invoiceClosed', handleInvoiceClosed)
  
  // Таймаут для очистки обработчика (на случай если ничего не произойдет)
  setTimeout(() => {
    if (!callbackExecuted) {
      console.warn('⚠️ Payment timeout - no response received')
      tg.offEvent('invoiceClosed', handleInvoiceClosed)
      callbackExecuted = true
      onPaymentResult({ success: false, error: 'Таймаут ожидания результата оплаты' })
    }
  }, 60000) // 60 секунд таймаут

  try {
    // Открываем инвойс с callback (если поддерживается)
    tg.openInvoice(invoiceUrl, callbackHandler)
    console.log('📱 Invoice opened:', invoiceUrl)
  } catch (error) {
    console.error('❌ Error opening invoice:', error)
    tg.offEvent('invoiceClosed', handleInvoiceClosed)
    callbackExecuted = true
    onPaymentResult({ success: false, error: 'Ошибка открытия платежа' })
  }
}

// Экспорт как объект для удобства
export default {
  fetchCrystalPackages,
  createInvoice,
  checkPaymentStatus,
  openTelegramPayment
}
